/**
 * Langues de l'interface : le français est la langue source (les textes du code servent de clés),
 * l'anglais vient du dictionnaire `i18n-en.ts`. Côté serveur, la langue par défaut est le français ;
 * `tr(lang, …)` traduit explicitement (e-mails, notifications).
 */
import { EN } from "./i18n-en";

export type Lang = "fr" | "en";
export const LANG_KEY = "gbc.lang";

export const langFromLocale = (locale: string | null | undefined): Lang => (locale?.toLowerCase().startsWith("en") ? "en" : "fr");
export const localeFromLang = (lang: Lang) => (lang === "en" ? "en-US" : "fr-FR");

function detect(): Lang {
  if (typeof window === "undefined") return "fr";
  try {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === "fr" || saved === "en") return saved;
  } catch {
    // Stockage indisponible (navigation privée) : on se fie au navigateur.
  }
  return /^fr\b/i.test(window.navigator?.language ?? "fr") ? "fr" : "en";
}

/** Langue de cette session (fixe : un changement de langue recharge la page). */
export const LANG: Lang = detect();
/** Locale pour Intl (dates, nombres, monnaies). */
export const LOCALE = localeFromLang(LANG);

/** Traduit `s` dans `lang`, puis remplace {0}, {1}… par les arguments. */
export function tr(lang: Lang, s: string, ...args: unknown[]): string {
  const base = lang === "en" ? (EN[s] ?? s) : s;
  return args.length ? base.replace(/\{(\d+)\}/g, (_, i: string) => String(args[Number(i)] ?? "")) : base;
}

/** Côté serveur : langue de la requête en cours (voir `server/lib/lang.ts`). */
let resolveLang: (() => Lang | undefined) | null = null;
export const setLangResolver = (fn: () => Lang | undefined) => {
  resolveLang = fn;
};
/** Langue active : celle de la requête (serveur) ou de la session (navigateur). */
export const currentLang = (): Lang => resolveLang?.() ?? LANG;

/** Traduit dans la langue active. */
export const t = (s: string, ...args: unknown[]) => tr(currentLang(), s, ...args);

/** Change la langue et recharge l'application. */
export function setLang(lang: Lang) {
  try {
    window.localStorage.setItem(LANG_KEY, lang);
  } catch {
    // ignoré
  }
  if (lang !== LANG) window.location.reload();
}
